import { AcceptInvite } from "@/components/AcceptInvite";
import { ApplicationsPage } from "@/components/ApplicationsPage";
import { AuthLayout } from "@/components/Auth";
import { Recovery } from "@/components/Auth/Recovery";
import { SignInPage, SignUpPage } from "@/components/Auth/SignIn";
import { Verification } from "@/components/Auth/Verification";
import { AuthenticatedLayout } from "@/components/AuthenticatedLayout";
import { CreateOrganizationPage } from "@/components/CreateOrganizationPage";
import { Dashboard } from "@/components/Dashboard";
import { DownForMaintenance } from "@/components/DownForMaintenance";
import { Error } from "@/components/Error";
import { SettingsPage } from "@/components/SettingsPage";
import { Toaster } from "@/components/ui/toaster";
import "@ory/elements-react/theme/styles.css";
import { RebootClientProvider } from "@reboot-dev/reboot-react";
import * as Sentry from "@sentry/browser";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./index.css";

Sentry.init({
  dsn: "https://6579eb84392ac0d6f60c77cd98df57e1@o4506991205023744.ingest.us.sentry.io/4506991206596608",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // TODO: reduce tracesSampleRate when we start having more traffic.
  tracesSampleRate: 1.0,
  // TODO(riley,rjh): Setting the following to be non-empty means adding
  //                  Sentry-specific headers to requests, which means CORS
  //                  policies need to be updated or we get request failures.
  //                  See:
  //                    https://docs.sentry.io/platforms/javascript/performance/instrumentation/automatic-instrumentation/#tracepropagationtargets
  //                  For now, we don't send these headers to any target, since
  //                  it's unclear that the resulting Sentry features are worth
  //                  it for us.
  tracePropagationTargets: [],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const url = import.meta.env.VITE_APP_REBOOT_ENDPOINT || "http://localhost:9991";

const DOWN_FOR_MAINTENANCE = false; // Set to `true` to take frontend down for maintenance.

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <RebootClientProvider url={url} key="RebootClientProvider">
      <BrowserRouter>
        <Routes>
          {DOWN_FOR_MAINTENANCE ? (
            <Route path="*" element={<DownForMaintenance />} />
          ) : (
            <>
              <Route path="/onboarding" element={<Dashboard />} />
              <Route path="/session" element={<Navigate to="/" replace />} />
              <Route element={<AuthenticatedLayout />}>
                <Route path="/" element={<ApplicationsPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
              <Route path="/login" element={<SignInPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route
                path="/verification"
                element={
                  <AuthLayout callToAction="Sign up">
                    <Verification />
                  </AuthLayout>
                }
              />
              <Route
                path="/recovery"
                element={
                  <AuthLayout callToAction="Sign in">
                    <Recovery />
                  </AuthLayout>
                }
              />
              <Route
                path="/create-organization"
                element={<CreateOrganizationPage />}
              />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/error" element={<Error />} />
            </>
          )}
        </Routes>
        <Toaster />
      </BrowserRouter>
    </RebootClientProvider>
  </StrictMode>
);
